import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/apiClient";
import type { PayoutRule, PayoutRuleKind } from "../api/types";
import { Modal } from "../components/Modal";

const KIND_LABELS: Record<PayoutRuleKind, string> = {
  PERCENT: "Percent of price",
  FLAT: "Flat per delivery",
  PER_KM: "Per kilometre",
};

function ruleFormula(rule: PayoutRule): string {
  if (rule.kind === "PERCENT") return `${rule.percent}% of the delivery price`;
  if (rule.kind === "FLAT") return `${rule.flatEur} € per delivery`;
  const base = rule.baseFeeEur ? `${rule.baseFeeEur} € base + ` : "";
  return `${base}${rule.ratePerKmEur} €/km`;
}

export function PayoutRulesPage() {
  const [rules, setRules] = useState<PayoutRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRules(await api.listPayoutRules());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load payout rules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const patch = async (
    rule: PayoutRule,
    payload: { isDefault?: boolean; isActive?: boolean },
  ) => {
    setBusyId(rule.id);
    setError(null);
    try {
      await api.updatePayoutRule(rule.id, payload);
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not update the rule.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Courier payout rules</h2>
          <p className="page-subtitle">
            Owner only. Amounts are fixed once created — to change a rate, create a
            new rule and deactivate the old one; past payouts never change.
          </p>
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <Link to="/app/dispatch" className="button plain">
            ← Back to dispatch
          </Link>
          <button type="button" className="button" onClick={() => setCreating(true)}>
            New rule
          </button>
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {loading ? <p className="muted">Loading…</p> : null}

      <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        {rules.length === 0 && !loading ? (
          <p className="muted">
            No payout rules yet. Without a rule, every assignment needs a manual
            payout amount.
          </p>
        ) : (
          rules.map((rule) => (
            <div key={rule.id} className="day-shifts-row">
              <div className="day-shifts-row-head">
                <div>
                  <strong>{rule.name}</strong>{" "}
                  <span className="muted">· {KIND_LABELS[rule.kind]}</span>
                </div>
                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                  {rule.isDefault ? <span className="badge primary">default</span> : null}
                  <span className={rule.isActive ? "badge success" : "badge muted"}>
                    {rule.isActive ? "active" : "inactive"}
                  </span>
                </div>
              </div>
              <p className="muted" style={{ margin: 0 }}>
                {ruleFormula(rule)}
                {rule.minPayoutEur ? ` · minimum ${rule.minPayoutEur} €` : ""}
              </p>
              <div className="button-row">
                {!rule.isDefault && rule.isActive ? (
                  <button
                    type="button"
                    className="button plain"
                    onClick={() => void patch(rule, { isDefault: true })}
                    disabled={busyId === rule.id}
                  >
                    Make default
                  </button>
                ) : null}
                <button
                  type="button"
                  className={rule.isActive ? "button danger" : "button plain"}
                  onClick={() => void patch(rule, { isActive: !rule.isActive })}
                  disabled={busyId === rule.id}
                >
                  {rule.isActive ? "Deactivate" : "Reactivate"}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {creating ? (
        <NewRuleModal
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      ) : null}
    </section>
  );
}

function NewRuleModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<PayoutRuleKind>("PERCENT");
  const [percent, setPercent] = useState("");
  const [flatEur, setFlatEur] = useState("");
  const [ratePerKmEur, setRatePerKmEur] = useState("");
  const [baseFeeEur, setBaseFeeEur] = useState("");
  const [minPayoutEur, setMinPayoutEur] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.createPayoutRule({
        name: name.trim(),
        kind,
        percent: kind === "PERCENT" && percent ? Number(percent) : undefined,
        flatEur: kind === "FLAT" && flatEur ? Number(flatEur) : undefined,
        ratePerKmEur:
          kind === "PER_KM" && ratePerKmEur ? Number(ratePerKmEur) : undefined,
        baseFeeEur: kind === "PER_KM" && baseFeeEur ? Number(baseFeeEur) : undefined,
        minPayoutEur:
          kind !== "FLAT" && minPayoutEur ? Number(minPayoutEur) : undefined,
        isDefault,
      });
      await onCreated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not create the rule.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New payout rule" onClose={onClose}>
      {error ? <div className="alert error">{error}</div> : null}
      <form onSubmit={(event) => void submit(event)}>
        <div className="modal-form">
          <div className="field full">
            <label htmlFor="rule-name">Name</label>
            <input
              id="rule-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "Standard 20%", "Bike flat 8"'
              disabled={busy}
              required
              maxLength={100}
            />
          </div>
          <div className="field">
            <label htmlFor="rule-kind">Model</label>
            <select
              id="rule-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as PayoutRuleKind)}
              disabled={busy}
            >
              <option value="PERCENT">{KIND_LABELS.PERCENT}</option>
              <option value="FLAT">{KIND_LABELS.FLAT}</option>
              <option value="PER_KM">{KIND_LABELS.PER_KM}</option>
            </select>
          </div>
          {kind === "PERCENT" ? (
            <div className="field">
              <label htmlFor="rule-percent">Percent (0-100)</label>
              <input
                id="rule-percent"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                disabled={busy}
                required
              />
            </div>
          ) : null}
          {kind === "FLAT" ? (
            <div className="field">
              <label htmlFor="rule-flat">Amount (EUR)</label>
              <input
                id="rule-flat"
                type="number"
                min="0"
                step="0.01"
                value={flatEur}
                onChange={(e) => setFlatEur(e.target.value)}
                disabled={busy}
                required
              />
            </div>
          ) : null}
          {kind === "PER_KM" ? (
            <>
              <div className="field">
                <label htmlFor="rule-rate">Rate (EUR/km)</label>
                <input
                  id="rule-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={ratePerKmEur}
                  onChange={(e) => setRatePerKmEur(e.target.value)}
                  disabled={busy}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="rule-base">Base fee (EUR)</label>
                <input
                  id="rule-base"
                  type="number"
                  min="0"
                  step="0.01"
                  value={baseFeeEur}
                  onChange={(e) => setBaseFeeEur(e.target.value)}
                  disabled={busy}
                />
              </div>
            </>
          ) : null}
          {kind !== "FLAT" ? (
            <div className="field">
              <label htmlFor="rule-min">Minimum payout (EUR)</label>
              <input
                id="rule-min"
                type="number"
                min="0"
                step="0.01"
                value={minPayoutEur}
                onChange={(e) => setMinPayoutEur(e.target.value)}
                disabled={busy}
              />
            </div>
          ) : null}
        </div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: "var(--space-3)",
          }}
        >
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            disabled={busy}
          />
          Default rule for couriers without an override
        </label>
        <div className="button-row" style={{ marginTop: "var(--space-3)" }}>
          <button type="button" className="button plain" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={busy}>
            {busy ? "Creating…" : "Create rule"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
